import { IMPORT_MAP } from '@app/importMap'

import type { ApiLocation } from '@app/packages/api/v2/types/entities'
import type { Address } from '@app/packages/geo/Address'
import { createAddress } from '@app/packages/geo/Address'
import type { Point } from '@app/packages/geo/Point'

import { createThunk } from '@app/store/thunk'

import type { AddressSuggestion } from './addressSuggestion'
import type { LabelExtractor, ReverseGeocodeOptions } from './addressTools'

export class AddressManager {
  address: Address

  constructor(address: Address) {
    this.address = address
  }

  isHouse() {
    return !!this.address.kind && ['house', 'entrance'].includes(this.address.kind)
  }

  /** A pin saved without an address: the user chose the place, and the geocoder has nothing to say about it. */
  isHouseOrPoint() {
    return this.isHouse() || this.address.isPoint === true
  }

  coordsArray(): LocationLatLonArray {
    return [this.address.location.lat, this.address.location.lon]
  }

  mapsBounds(): [LocationLatLonArray, LocationLatLonArray] | undefined {
    if (!this.address.bounds) return undefined
    return [
      [this.address.bounds.bottomLeft.lat, this.address.bounds.bottomLeft.lon],
      [this.address.bounds.topRight.lat, this.address.bounds.topRight.lon],
    ]
  }

  asRectangle(): [[number, number], [number, number]] | undefined {
    if (!this.address.bounds) return undefined
    return [
      [this.address.bounds.bottomLeft.lat, this.address.bounds.bottomLeft.lon],
      [this.address.bounds.topRight.lat, this.address.bounds.topRight.lon],
    ]
  }

  asLocation() {
    return {
      address: this.address.label ?? '',
      king: this.address.kind,
      latitude: this.address.location.lat,
      longitude: this.address.location.lon,
    }
  }

  static fromLegacyJSON(data: LegacyAddressJSON) {
    return new AddressManager(
      createAddress({
        label: data.address,
        kind: data.kind,
        location: {
          lat: data.latitude,
          lon: data.longitude,
        },
        bounds:
          data.lowerCorner && data.upperCorner
            ? {
                bottomLeft: {
                  lat: data.lowerCorner[0],
                  lon: data.lowerCorner[1],
                },
                topRight: {
                  lat: data.upperCorner[0],
                  lon: data.upperCorner[1],
                },
              }
            : undefined,
      })
    )
  }

  /** A bare suggestion — a string nobody suggested — is looked up by its text. */
  static fromSuggestion(suggestion: AddressSuggestion, extractLabel?: LabelExtractor) {
    return createThunk(async dispatch => {
      const { getSuggestionGeocodeData } = await IMPORT_MAP.utils.addressTools()
      const address = await dispatch(getSuggestionGeocodeData(suggestion, { extractLabel }))
      if (!address) return null
      return new AddressManager(
        createAddress({
          label: address.label,
          kind: address.kind,
          location: address.location,
          bounds: address.bounds,
        })
      )
    })
  }

  static fromPoint(point: Point, options?: ReverseGeocodeOptions) {
    return createThunk(async dispatch => {
      const { getReverseGeocodeData } = await IMPORT_MAP.utils.addressTools()
      const address = await dispatch(getReverseGeocodeData(point, options))
      if (!address) return null
      return new AddressManager(
        createAddress({
          label: address.label,
          kind: address.kind,
          location: address.location,
          bounds: address.bounds,
        })
      )
    })
  }

  static fromLocation(location: ApiLocation['attributes']) {
    return new AddressManager(
      createAddress({
        label: location.address,
        location: {
          lat: location.latitude,
          lon: location.longitude,
        },
      })
    )
  }
}

type LocationLatLonArray = [latitude: number, longitude: number]

type LegacyAddressJSON = {
  latitude: number
  longitude: number
  address?: string
  kind?: string
  lowerCorner?: LocationLatLonArray
  upperCorner?: LocationLatLonArray
}
